import * as React from "react";
import {connect, Dispatch} from "react-redux";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import {RootState} from "../../../store/reducer/root/RootState";
import DictionarySortableItemsList from "../common/DictionarySortableItemsList";
import AssetCategory from "../../../model/asset/AssetCategory";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {
	createAssetCategory,
	updateAssetCategoriesSortOrder,
	updateAssetCategory
} from "../../../store/action/assetCategory/assetCategory";
import DictionaryItem from "../../../model/dictionary/DictionaryItem";
import Toast from "../../helper/Toast";
import TextUtils from "../../../utils/TextUtils";
import {AssetCategoriesListFilter} from "../../../store/local/model/AssetCategoriesListFilter";
import LocalStorage from "../../../store/local/LocalStorage";
import Switch from "../../../hiberUiKit/src/atoms/Switch/Switch";
import ButtonGroupWrapper from "../../../hiberUiKit/src/atoms/Buttons/ButtonGroupWrapper";


interface ReduxProps {
	/**
	 * All asset categories.
	 */
	assetCategories: AssetCategory[];
}


interface DispatchProps {
	/**
	 * Sort order update callback.
	 * @param {AssetCategory[]} assetCategories
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	updateSortOrder: (assetCategories: AssetCategory[], onSuccess: OnSuccess, onFailure: OnFailure) => any;
	/**
	 * Create asset category.
	 * @param {AssetCategory} assetCategory
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	createAssetCategory: (assetCategory: AssetCategory, onSuccess: OnSuccess, onFailure: OnFailure) => any;
	/**
	 * Update asset category.
	 * @param {string} acId
	 * @param {AssetCategory} assetCategory
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	updateAssetCategory: (acId: string,
		assetCategory: AssetCategory,
		onSuccess: OnSuccess,
		onFailure: OnFailure) => any;
}

interface IState {
	/**
	 * Asset categories list filter.
	 */
	filter: AssetCategoriesListFilter;
}

type Props = ReduxProps & DispatchProps;

/**
 * Asset category config view.
 */
class AssetCategoryConfigView extends React.Component<Props, IState> {

	constructor(props: Props) {
		super(props);

		this.state = {
			filter: LocalStorage.getAssetCategoriesListFilter()
		};

		this.renderRowBody = this.renderRowBody.bind(this);
		this.renderFilters = this.renderFilters.bind(this);
		this.updateAssetCategory = this.updateAssetCategory.bind(this);
		this.updateSortOrder = this.updateSortOrder.bind(this);
		this.changeStatus = this.changeStatus.bind(this);
	}

	render() {
		const assetCategories = this.props.assetCategories.filter(
			ac => this.state.filter.hideDisabledAssetCategories ? !ac.disabled : true);
		return (
			<DictionarySortableItemsList title={"Asset Category"}
										 items={assetCategories}
										 updateItem={this.updateAssetCategory}
										 createItem={this.props.createAssetCategory}
										 updateSortOrder={this.updateSortOrder}
										 renderFilters={this.renderFilters}
										 renderRowBody={this.renderRowBody}
										 renderHeaderBody={this.renderHeaderBody}/>
		)
	}

	updateAssetCategory(itemId: string, item: DictionaryItem, onSuccess: OnSuccess, onFailure: OnFailure) {
		this.props.updateAssetCategory(itemId,
			this.props.assetCategories.filter(ac => ac.id === itemId)[0].copy({name: item.name}), onSuccess, onFailure)
	}

	updateSortOrder(assetCategories: AssetCategory[], onSuccess: OnSuccess, onFailure: OnFailure) {
		if (this.state.filter.hideDisabledAssetCategories) {
			assetCategories = assetCategories.concat(this.props.assetCategories.filter(ac => ac.disabled));
		}
		this.props.updateSortOrder(assetCategories, onSuccess, onFailure);
	}

	renderFilters() {
		return (
			<ButtonGroupWrapper isRight>
				<Switch label={"Hide disabled Asset Categories"}
						checked={this.state.filter.hideDisabledAssetCategories}
						onChange={(checked) => this.setState({filter: this.state.filter.updateDisabledAssetCategories(checked)})}
				/>
			</ButtonGroupWrapper>
		)
	}

	renderRowBody(item: AssetCategory) {
		return (
			<React.Fragment>
				<td style={{width: "40%"}}>
					<span style={{cursor: "pointer"}} onClick={(e) => this.onIdClick(e, item)}>{item.id}</span>
				</td>
				<td style={{width: "40%"}}>{item.name}</td>
				<td style={{width: "20%"}}>
					{item.disabled ?
						<ButtonPrimary label={"Enable"}
									   onClick={(e) => this.changeStatus(e, item.copy({disabled: false}))}/> :
						<ButtonPrimary label={"Disable"}
									   onClick={(e) => this.changeStatus(e, item.copy({disabled: true}))}/>}
				</td>
			</React.Fragment>
		)
	}

	onIdClick(e: any, item: AssetCategory) {
		e.stopPropagation();
		TextUtils.copyToClipboard(item.id);
		Toast.success("Asset category id copied successfully", "Copy to clipboard");
	}

	renderHeaderBody() {
		return (
			<React.Fragment>
				<th>Id</th>
				<th>Name</th>
				<th>Actions</th>
			</React.Fragment>
		)
	}

	changeStatus(e: React.MouseEvent<HTMLButtonElement>, item: AssetCategory) {
		e.stopPropagation();
		const title = `Asset Category ${item.disabled ? "disabled" : "enabled"}`;

		this.props.updateAssetCategory(item.id, item,
			() => Toast.success(title, title),
			() => Toast.error("Sorry, something went wrong. Try again later.", title))
	}
}

function mapStateToProps(state: RootState) {
	return {
		assetCategories: [].concat(state.assetCategory.assetCategories.getOrElse([]))
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateSortOrder: (assetCategories: AssetCategory[], onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateAssetCategoriesSortOrder(assetCategories, onSuccess, onFailure)
		),
		createAssetCategory: (assetCategory: AssetCategory, onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(createAssetCategory(assetCategory, onSuccess, onFailure)),
		updateAssetCategory: (acId: string,
			assetCategory: AssetCategory,
			onSuccess: OnSuccess,
			onFailure: OnFailure) =>
			dispatch(updateAssetCategory(acId, assetCategory, onSuccess, onFailure))
	}
}

export default connect<ReduxProps, DispatchProps, null>(mapStateToProps, mapDispatchToProps)(AssetCategoryConfigView)